import type { Ordre } from '@hector/shared-types';

export type { Ordre };

export type Jurisdiction = 'CC' | 'CA' | 'TJ' | 'TCOM' | 'CE' | 'CAA' | 'TA';

export const JURISDICTION_LABELS: Record<Jurisdiction, string> = {
  CC: 'Cour de cassation',
  CA: "Cour d'appel",
  TJ: 'Tribunal judiciaire',
  TCOM: 'Tribunal de commerce',
  CE: "Conseil d'État",
  CAA: "Cour administrative d'appel",
  TA: 'Tribunal administratif',
};

/** Juridictions de l'ordre judiciaire — sélectionnées par défaut */
export const ALL_JURISDICTIONS: Jurisdiction[] = ['CC', 'CA', 'TJ', 'TCOM'];

/** Juridictions de l'ordre administratif — opt-in */
export const ADMIN_JURISDICTIONS: Jurisdiction[] = ['CE', 'CAA', 'TA'];

export const JURISDICTION_COLORS: Record<Jurisdiction, string> = {
  // Ordre judiciaire
  CC: 'bg-blue-50 text-accent-blue border-blue-200',
  CA: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  TJ: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  TCOM: 'bg-amber-50 text-amber-700 border-amber-200',
  // Ordre administratif
  CE: 'bg-purple-50 text-purple-700 border-purple-200',
  CAA: 'bg-violet-50 text-violet-700 border-violet-200',
  TA: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
};

export interface LegalQuestion {
  id: string;
  text: string;
  /** 3-5 lines explaining why this question is asked and how it relates to the case */
  context?: string;
  /** @deprecated Kept for backwards compat — pipeline now uses root-level jurisdictions */
  jurisdictions: Jurisdiction[];
  /** Article references linked to this question (e.g. "code_civil:1240") */
  articleReferences?: string[];
}

export interface FondementJuridique {
  /** Canonical key (e.g. "code_civil:1240") — must not be edited by user */
  key: string;
  /** Display name (e.g. "Code civil") */
  codeName: string;
  /** Display number (e.g. "1240", "L. 442-6") */
  articleNumber: string;
  /** Article text content — may be empty initially */
  content: string;
}

/**
 * Maps Judilibre chamber codes to readable French labels.
 * API codes: pl, mi, civ1, civ2, civ3, comm, soc, cr, creun, ordo, allciv, other
 * Source: Judilibre API validation — sandbox-api.piste.gouv.fr
 */
export const CHAMBER_LABELS: Record<string, string> = {
  // Cour de cassation — codes API Judilibre
  civ1: '1re chambre civile',
  civ2: '2e chambre civile',
  civ3: '3e chambre civile',
  comm: 'Chambre commerciale',
  soc: 'Chambre sociale',
  cr: 'Chambre criminelle',
  mi: 'Chambre mixte',
  pl: 'Assemblée plénière',
  ordo: 'Ordonnance',
  creun: 'Chambres réunies',
  allciv: 'Chambres civiles',
  other: 'Autre formation',
  // Cours d'appel — libellés longs envoyés par le backend
  'chambre sociale': 'Chambre sociale',
  'chambre commerciale': 'Chambre commerciale',
  'chambre civile': 'Chambre civile',
  'chambre correctionnelle': 'Chambre correctionnelle',
  'chambre des appels correctionnels': 'Chambre des appels correctionnels',
  'chambre de l\'instruction': "Chambre de l'instruction",
  'chambre de la famille': 'Chambre de la famille',
};

/** Resolve a chamber value to a short readable label */
export function formatChamber(raw?: string): string | undefined {
  if (!raw) return undefined;
  const key = raw.toLowerCase().trim();
  return CHAMBER_LABELS[key] ?? raw;
}

export interface QuestionProgress {
  id: string;
  questionText: string;
  progress: number; // 0-100
  phase?: string;
}

/** Single source decision — matches backend wire format (snake_case) */
export interface JurisprudenceSource {
  id: string;
  reference: string;
  jurisdiction: Jurisdiction;
  pertinence_score: number; // 0–1
  judilibre_url?: string;
  official_summary?: string;
  date?: string;
  chamber?: string;
  /** LLM-formatted title (e.g. "CA Paris, 15 janvier 2023, n° 21/12345") */
  title?: string;
  /** Mistral pertinence analysis (3-5 sentences) */
  reasoning?: string;
  /** Geographic identifier (e.g. "ca_poitiers", "tj_paris") — absent for Cour de cassation */
  location?: string;
  /**
   * Ordre juridictionnel — source autoritaire pour discriminer le viewer à ouvrir.
   * WHY: `jurisdiction` (CE/CAA/TA) était utilisé pour détecter les décisions admin
   * mais c'est une dérivation divergente. `ordre` est estampillé par le backend
   * depuis `AnalyzedDecision.ordre` (lui-même issu de `Step1MetaRow.ordre`) et
   * ne peut pas être faussé par un libellé de juridiction mal défauté. (D7, 2026-06-18)
   */
  ordre?: Ordre;
}
